import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { useConfigurationStore } from '@/stores/configuration';
import { APP_VERSION, APP_VERSION_HASH, BUILD_ENV } from '@/utils/constants';
import { getStandaloneMode } from '@/utils/get-standalone-mode';

export const SENTRY_LOG = {
  UPLOAD: 'UPLOAD',
  WEBSOCKET: 'WEBSOCKET',
  KICKOUT: 'KICKOUT',
  STORAGE: 'STORAGE',
  DOWNLOAD: 'DOWNLOAD',
  MESSAGES: 'MESSAGES',
  RELOAD: 'RELOAD',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
} as const;

export type SENTRY_LOG_TYPE = keyof typeof SENTRY_LOG;

export interface SentryLogType<TResult> {
  name: SENTRY_LOG_TYPE;
  op?: string;
  action: () => Promise<TResult>;
  requestAttributes?: {
    [key: string]: unknown;
  };
}

export type SentrySimpleLogType = Pick<SentryLogType<void>, 'name' | 'op' | 'requestAttributes'>;

export default async function initializeSentry() {
  console.info('[Sentry] initializeSentry');

  if (Sentry.getClient()) return;
  console.info('[Sentry] getClient');

  const config = useConfigurationStore.getState().envConfig.sentry;
  if (!config?.enable || !config.dsn) return;

  const isStandalone = getStandaloneMode();
  const isEmbedded = BUILD_ENV === 'embedded';
  const spanMappping: { [key: string]: number } = {};

  Sentry.init({
    ...config,
    environment: config.environment ?? undefined,
    release: `${config.release}@${APP_VERSION}`,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserSessionIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.httpClientIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    initialScope: {
      tags: { plat: isEmbedded ? 'embed' : isStandalone ? 'PWA' : 'Web', app_version: APP_VERSION_HASH },
    },
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'window.webkit.messageHandlers',
      'UCShellJava.sdkEventFire',
      'getdetailsourceid',
      'QK_middlewareReadModePageDetect',
      'avast',
      'file://',
      'translate.goog',
      'zaloJSV2',
      'ceCurrentVideo',
      'instantSearchSDKJSBridgeClearHighlight',
      // NOTE: app specific
      'NOT_AUTHENTICATED',
    ],
    beforeSendSpan(span) {
      const path = `${location.pathname}${location.search}`.replace('auth', '<hash>');

      const currentTime = Date.now();
      const lastTime = spanMappping[span.description || ''] || 0;

      if (currentTime - lastTime < 1000) return null;
      spanMappping[span.description || ''] = currentTime;

      span.data = {
        ...(span.data || {}),
        path_s: path,
      };

      Sentry.setContext('chat_info', {
        path_c: path,
      });

      return span;
    },
    beforeSendTransaction(transaction) {
      const path = `${location.pathname}${location.search}`.replace('auth', '<hash>');

      transaction.extra = {
        ...(transaction.extra || {}),
        path_t: path,
      };

      return transaction;
    },
    tracesSampler: () =>
      useAuthStore.getState().auth?.TraceRate ?? Sentry.getClient()?.getOptions().tracesSampleRate ?? 0.01,
  });
}

export function promiseLogger<TResult>({ action, name, requestAttributes = {} }: SentryLogType<TResult>) {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: `${name}`,
    },
    (span) => {
      for (const key in requestAttributes) {
        span.setAttribute(`http.request.${key}`, String(requestAttributes[key]));
      }
      return action();
    },
  );
}

export function simpleLogger({ name, op, requestAttributes = {} }: SentrySimpleLogType) {
  Sentry.startSpanManual({ op: op || 'http.client', name: `${name}` }, (span) => {
    for (const key in requestAttributes) {
      span.setAttribute(key, String(requestAttributes[key]));
    }
    span.end();
  });
}
